import React, { useState, useMemo } from 'react';
import {
  FileType, Type, Trash2, ChevronRight, Box, Lightbulb, Package,
  Info, Sparkles, FileBox, Palette, Ruler, Plus, ArrowUp, ArrowDown,
  Layers, Eye, EyeOff, Zap, Tag, Printer, Download, FolderArchive,
  Scissors, Link2, Unlink2,
} from 'lucide-react';
import useEditorStore from '../store/useEditorStore';
import {
  STRUCTURE_TYPES_FR, FONTS, MATERIAL_TYPES,
  getColorsForMaterial,
} from '../mock/mock';
import { toast } from 'sonner';
import { exportProjectPDF } from '../services/pdfExport';
import { exportProductionZip } from '../services/productionExport';
import ProjectsPanel from './ProjectsPanel';
import ProjectMetaPanel from './ProjectMetaPanel';

function HelpDot({ tip }) { return <span className="help-icon" title={tip}>?</span>; }

function Slider({ label, value, min, max, step = 1, suffix = '', onChange }) {
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <div className="slider-row">
      <label>{label}<b>{value}{suffix}</b></label>
      <input
        type="range" className="pro-slider"
        min={min} max={max} step={step} value={value}
        style={{ '--val': `${pct}%` }}
        onChange={(e) => onChange(parseFloat(e.target.value))}
      />
    </div>
  );
}

function ColorSwatchGrid({ colors, selectedHex, onPick }) {
  return (
    <div className="swatch-grid">
      {colors.map((c) => (
        <button
          key={c.id}
          className={`swatch ${selectedHex?.toLowerCase() === c.hex.toLowerCase() ? 'selected' : ''}`}
          style={{ background: c.preview || c.hex }}
          title={c.name}
          onClick={() => onPick(c)}
        />
      ))}
    </div>
  );
}

function PartEditor({ partKey, label, segment, onChangeMaterial, onChangeColor }) {
  const matId = segment.materials?.[partKey] || (partKey === 'front' ? 'pmma' : 'petg');
  const swatches = getColorsForMaterial(matId);
  const currentMat = MATERIAL_TYPES.find((m) => m.id === matId) || MATERIAL_TYPES[0];
  return (
    <div className="part-editor">
      <div className="part-editor-head">
        <span className="part-editor-title">{label}</span>
      </div>
      <div className="material-row">
        {MATERIAL_TYPES.map((m) => (
          <button
            key={m.id}
            className={`material-chip ${m.id === matId ? 'selected' : ''}`}
            onClick={() => onChangeMaterial(partKey, m.id)}
            title={m.desc}
          >
            <span className="material-swatch" style={{ background: m.swatchBg }} />
            <span className="material-name">{m.name.split(' ')[0]}</span>
          </button>
        ))}
      </div>
      <div className="current-mat-info">{currentMat.desc}</div>
      <ColorSwatchGrid
        colors={swatches}
        selectedHex={segment.colors[partKey]}
        onPick={(c) => onChangeColor(partKey, c.hex)}
      />
      <div className="slider-row" style={{ marginTop: 6 }}>
        <label>Personnalisée</label>
        <input
          type="color" className="color-input"
          value={segment.colors[partKey]}
          onChange={(e) => onChangeColor(partKey, e.target.value)}
        />
      </div>
    </div>
  );
}

function estimateLedLength(segments, dims, density) {
  const charCount = segments.reduce((acc, s) => acc + (s.text?.trim().length || 0), 0);
  if (charCount === 0) return { lengthM: 0, ledCount: 0, spacing: 30 };
  const h = dims.height;
  const charArea = h * h * 0.55;
  const spacing = 30;
  const totalArea = charArea * charCount;
  const stripMm = (totalArea / spacing);
  const lengthM = stripMm / 1000;
  const ledCount = Math.ceil(lengthM * density);
  return { lengthM: +lengthM.toFixed(2), ledCount, spacing };
}

// === Material cost estimation ===
// PETG: 3D printing - volume × density (1.27 g/cm³) = grams → kg
// PMMA / Bois / PVC: découpe laser - surface 2D × prix/cm²
const PETG_DENSITY = 1.27; // g/cm³
const MAT_LABELS = { petg: 'PETG', pmma: 'PMMA', pvc: 'PVC', wood: 'Bois' };

function estimateCosts(segments, dims, structureType, pricing) {
  const charCount = segments.reduce((acc, s) => acc + (s.text?.trim().length || 0), 0);
  const hmm = dims.height; // mm
  // Surface 2D approx of all text in cm²
  const charAreaCm2 = (hmm * hmm * 0.55) / 100; // mm² → cm²
  const totalSurfaceCm2 = charAreaCm2 * charCount;
  const depthCm = dims.depth / 10;
  const wallThkCm = dims.wallThickness / 10;
  const acrThkCm = dims.acrylicThickness / 10;
  const backThkCm = 0.3; // 3mm default
  // Per-segment cost; we sum by material across all parts
  const bom = []; // bill of materials
  segments.forEach((seg) => {
    const segChars = (seg.text || '').trim().length;
    if (!segChars) return;
    const segSurfaceCm2 = charAreaCm2 * segChars;
    // BACK plate
    const backMat = seg.materials?.back || 'petg';
    const backVolCm3 = segSurfaceCm2 * backThkCm;
    bom.push({ segText: seg.text, part: 'back', material: backMat, surfaceCm2: segSurfaceCm2, volumeCm3: backVolCm3 });
    // WALLS (hollow extrusion approx: perimeter × wall × depth)
    // Approx perimeter for letters: surface / (typicalCharWidth/2) ≈ 4 × sqrt(surface)
    // Simpler: walls volume ≈ surface × depth × 0.18 (18% material occupation)
    const wallsMat = seg.materials?.walls || 'petg';
    const wallsVolCm3 = segSurfaceCm2 * depthCm * 0.18;
    bom.push({ segText: seg.text, part: 'walls', material: wallsMat, surfaceCm2: segSurfaceCm2 * 0.6, volumeCm3: wallsVolCm3 });
    // FRONT face
    if (structureType !== 'C') {
      const frontMat = seg.materials?.front || 'pmma';
      const frontVolCm3 = segSurfaceCm2 * acrThkCm;
      bom.push({ segText: seg.text, part: 'front', material: frontMat, surfaceCm2: segSurfaceCm2 * 0.95, volumeCm3: frontVolCm3 });
    } else {
      const frontMat = seg.materials?.front || 'petg';
      bom.push({ segText: seg.text, part: 'front', material: frontMat, surfaceCm2: segSurfaceCm2, volumeCm3: segSurfaceCm2 * 0.2 });
    }
  });
  // Aggregate per material
  const byMat = {};
  bom.forEach((item) => {
    if (!byMat[item.material]) byMat[item.material] = { surfaceCm2: 0, volumeCm3: 0 };
    byMat[item.material].surfaceCm2 += item.surfaceCm2;
    byMat[item.material].volumeCm3 += item.volumeCm3;
  });
  // Compute costs
  const lines = [];
  let totalCost = 0;
  Object.entries(byMat).forEach(([mat, agg]) => {
    if (mat === 'petg') {
      const weightG = agg.volumeCm3 * PETG_DENSITY;
      const weightKg = weightG / 1000;
      const cost = weightKg * pricing.petgPerKg;
      lines.push({ material: 'PETG', kg: +weightKg.toFixed(3), surfaceCm2: 0, cost: +cost.toFixed(2), unit: `${weightKg.toFixed(3)} kg \u00d7 ${pricing.petgPerKg}\u20ac/kg` });
      totalCost += cost;
    } else if (mat === 'pmma') {
      const cost = agg.surfaceCm2 * pricing.pmmaPerCm2;
      lines.push({ material: 'PMMA', kg: 0, surfaceCm2: +agg.surfaceCm2.toFixed(1), cost: +cost.toFixed(2), unit: `${agg.surfaceCm2.toFixed(1)} cm\u00b2 \u00d7 ${pricing.pmmaPerCm2}\u20ac/cm\u00b2` });
      totalCost += cost;
    } else if (mat === 'wood') {
      const cost = agg.surfaceCm2 * pricing.woodPerCm2;
      lines.push({ material: 'Bois', kg: 0, surfaceCm2: +agg.surfaceCm2.toFixed(1), cost: +cost.toFixed(2), unit: `${agg.surfaceCm2.toFixed(1)} cm\u00b2 \u00d7 ${pricing.woodPerCm2}\u20ac/cm\u00b2` });
      totalCost += cost;
    } else if (mat === 'pvc') {
      const cost = agg.surfaceCm2 * pricing.pvcPerCm2;
      lines.push({ material: 'PVC', kg: 0, surfaceCm2: +agg.surfaceCm2.toFixed(1), cost: +cost.toFixed(2), unit: `${agg.surfaceCm2.toFixed(1)} cm\u00b2 \u00d7 ${pricing.pvcPerCm2}\u20ac/cm\u00b2` });
      totalCost += cost;
    }
  });
  // Labor estimate (printing time ~30min per char @ 0.5h)
  const laborHours = +(charCount * 0.4).toFixed(2);
  const laborCost = laborHours * pricing.laborPerHour;
  // Margin
  const subtotal = totalCost + laborCost;
  const marginAmount = subtotal * (pricing.marginPct / 100);
  const totalHT = subtotal + marginAmount;
  return {
    lines,
    laborHours,
    laborCost: +laborCost.toFixed(2),
    materialsCost: +totalCost.toFixed(2),
    marginAmount: +marginAmount.toFixed(2),
    totalHT: +totalHT.toFixed(2),
    totalTTC: +(totalHT * 1.2).toFixed(2),
  };
}

function PrintSettingsPanel() {
  const ps = useEditorStore((s) => s.printSettings);
  const setPS = useEditorStore((s) => s.setPrintSetting);
  const printer = useEditorStore((s) => s.printer);
  const applyPrinterPreset = useEditorStore((s) => s.applyPrinterPreset);
  const applyQualityPreset = useEditorStore((s) => s.applyQualityPreset);
  const socleRim = useEditorStore((s) => s.socleRim);
  const setSocleRim = useEditorStore((s) => s.setSocleRim);
  const mountingHoles = useEditorStore((s) => s.mountingHoles);
  const setMountingHoles = useEditorStore((s) => s.setMountingHoles);
  const cableHole = useEditorStore((s) => s.cableHole);
  const setCableHole = useEditorStore((s) => s.setCableHole);
  const ledOn = useEditorStore((s) => s.ledOn);

  return (
    <div className="print-settings">
      <div className="ps-section-title">Modèle d'imprimante</div>
      <div className="printer-presets" data-testid="printer-presets">
        <button
          className={`printer-preset-btn ${printer.id === 'k2plus' ? 'active' : ''}`}
          onClick={() => { applyPrinterPreset('k2plus'); toast.success('Preset Creality K2 Plus Combo appliqué (350×350×350)'); }}
          data-testid="printer-preset-k2plus"
        >
          <b>K2 Plus Combo</b><span>350 × 350 × 350 mm</span>
        </button>
        <button
          className={`printer-preset-btn ${printer.id === 'k1' ? 'active' : ''}`}
          onClick={() => { applyPrinterPreset('k1'); toast.success('Preset Creality K1 appliqué (220×220×250)'); }}
          data-testid="printer-preset-k1"
        >
          <b>K1</b><span>220 × 220 × 250 mm</span>
        </button>
      </div>
      <div className="ps-grid-3">
        <div className="ps-field"><label>X max</label><input type="number" value={printer.maxX} disabled /></div>
        <div className="ps-field"><label>Y max</label><input type="number" value={printer.maxY} disabled /></div>
        <div className="ps-field"><label>Z max</label><input type="number" value={printer.maxZ} disabled /></div>
      </div>
      <div className="ps-note">Les pièces &gt; {printer.maxX}×{printer.maxY}×{printer.maxZ} mm sont automatiquement découpées en sous-éléments.</div>

      <div className="ps-section-title">Qualité d'impression</div>
      <div className="quality-presets" data-testid="quality-presets">
        {[
          { id: 'draft', label: 'Brouillon', sub: '0.28 mm' },
          { id: 'standard', label: 'Standard', sub: '0.20 mm' },
          { id: 'fine', label: 'Fine', sub: '0.12 mm' },
          { id: 'ultra', label: 'Ultra', sub: '0.08 mm' },
        ].map((q) => (
          <button
            key={q.id}
            className={`quality-btn ${Math.abs((ps.layerHeight) - parseFloat(q.sub)) < 0.01 ? 'active' : ''}`}
            onClick={() => { applyQualityPreset(q.id); toast.success(`Profil "${q.label}" appliqué`); }}
            data-testid={`quality-${q.id}`}
          >
            <b>{q.label}</b><span>{q.sub}</span>
          </button>
        ))}
      </div>

      <div className="ps-section-title">Couches & parois</div>
      <Slider label="Buse Ø" suffix=" mm" value={ps.nozzleDiameter} min={0.2} max={1.0} step={0.05}
        onChange={(v) => setPS('nozzleDiameter', v)} />
      <Slider label="Hauteur de couche" suffix=" mm" value={ps.layerHeight} min={0.08} max={0.4} step={0.02}
        onChange={(v) => setPS('layerHeight', v)} />
      <Slider label="1ère couche" suffix=" mm" value={ps.firstLayerHeight} min={0.1} max={0.4} step={0.02}
        onChange={(v) => setPS('firstLayerHeight', v)} />
      <Slider label="Parois (perimeters)" value={ps.wallLineCount} min={1} max={8} step={1}
        onChange={(v) => setPS('wallLineCount', v)} />
      <Slider label="Couches haut/bas" value={ps.topBottomLayers} min={2} max={10} step={1}
        onChange={(v) => setPS('topBottomLayers', v)} />

      <div className="ps-section-title">Remplissage</div>
      <Slider label="Taux de remplissage" suffix=" %" value={ps.infillPercent} min={0} max={100} step={5}
        onChange={(v) => setPS('infillPercent', v)} />
      <div className="ps-field">
        <label>Motif d'infill</label>
        <select value={ps.infillPattern} onChange={(e) => setPS('infillPattern', e.target.value)} className="ps-select" data-testid="ps-infill-pattern">
          <option value="grid">Grille</option>
          <option value="gyroid">Gyroïde (recommandé pour solidité)</option>
          <option value="triangles">Triangles</option>
          <option value="honeycomb">Nid d'abeille</option>
          <option value="cubic">Cubique</option>
        </select>
      </div>

      <div className="ps-section-title">Vitesses</div>
      <Slider label="Vitesse impression" suffix=" mm/s" value={ps.printSpeed} min={20} max={500} step={5}
        onChange={(v) => setPS('printSpeed', v)} />
      <Slider label="Vitesse 1ère couche" suffix=" mm/s" value={ps.firstLayerSpeed} min={10} max={80} step={5}
        onChange={(v) => setPS('firstLayerSpeed', v)} />
      <Slider label="Vitesse déplacements" suffix=" mm/s" value={ps.travelSpeed} min={50} max={600} step={10}
        onChange={(v) => setPS('travelSpeed', v)} />

      <div className="ps-section-title">Températures</div>
      <Slider label="Buse" suffix=" °C" value={ps.nozzleTemp} min={180} max={320} step={5}
        onChange={(v) => setPS('nozzleTemp', v)} />
      <Slider label="Plateau" suffix=" °C" value={ps.bedTemp} min={0} max={120} step={5}
        onChange={(v) => setPS('bedTemp', v)} />
      <Slider label="Ventilateur" suffix=" %" value={ps.fanSpeed} min={0} max={100} step={5}
        onChange={(v) => setPS('fanSpeed', v)} />

      <div className="ps-section-title">Adhérence & supports</div>
      <div className="ps-field">
        <label>Adhérence plateau</label>
        <select value={ps.adhesion} onChange={(e) => setPS('adhesion', e.target.value)} className="ps-select" data-testid="ps-adhesion">
          <option value="none">Aucune</option>
          <option value="skirt">Skirt</option>
          <option value="brim">Brim (recommandé pour PETG)</option>
          <option value="raft">Raft</option>
        </select>
      </div>
      <label className="ps-toggle">
        <input type="checkbox" checked={ps.enableSupports} onChange={(e) => setPS('enableSupports', e.target.checked)} />
        Activer les supports
      </label>
      {ps.enableSupports && (
        <Slider label="Angle supports" suffix=" °" value={ps.supportAngle} min={20} max={80} step={5}
          onChange={(v) => setPS('supportAngle', v)} />
      )}

      <div className="ps-section-title">Rétraction & filament</div>
      <label className="ps-toggle">
        <input type="checkbox" checked={ps.retractionEnabled} onChange={(e) => setPS('retractionEnabled', e.target.checked)} />
        Activer la rétraction
      </label>
      {ps.retractionEnabled && (
        <>
          <Slider label="Distance rétract." suffix=" mm" value={ps.retractionDistance} min={0.2} max={8} step={0.1}
            onChange={(v) => setPS('retractionDistance', v)} />
          <Slider label="Vitesse rétract." suffix=" mm/s" value={ps.retractionSpeed} min={10} max={120} step={5}
            onChange={(v) => setPS('retractionSpeed', v)} />
        </>
      )}
      <div className="ps-field">
        <label>Filament</label>
        <input type="text" value={ps.filamentBrand} onChange={(e) => setPS('filamentBrand', e.target.value)} />
      </div>
      <Slider label="Diamètre filament" suffix=" mm" value={ps.filamentDiameter} min={1.5} max={3.0} step={0.05}
        onChange={(v) => setPS('filamentDiameter', v)} />

      {/* ===== Débord (rim) socle pour clipsage face ===== */}
      <div className="ps-section-title">Débord socle (clipsage face)</div>
      <label className="ps-toggle">
        <input type="checkbox" checked={socleRim.enabled} onChange={(e) => setSocleRim({ enabled: e.target.checked })} data-testid="rim-enabled" />
        Débord interne pour pose de la face PMMA
      </label>
      {socleRim.enabled && (
        <>
          <Slider label="Largeur débord" suffix=" mm" value={socleRim.width} min={1} max={15} step={0.5}
            onChange={(v) => setSocleRim({ width: v })} />
          <Slider label="Épaisseur débord" suffix=" mm" value={socleRim.depth} min={0.5} max={6} step={0.1}
            onChange={(v) => setSocleRim({ depth: v })} />
          <Slider label="Jeu d'insertion" suffix=" mm" value={socleRim.clearance} min={0} max={1} step={0.05}
            onChange={(v) => setSocleRim({ clearance: v })} />
          <div className="ps-note">La face avant s'emboîte par-dessus le débord (clipsage léger avec jeu de {socleRim.clearance}mm).</div>
        </>
      )}

      {/* ===== Trous de fixation ===== */}
      <div className="ps-section-title">Trous de fixation murale</div>
      <label className="ps-toggle">
        <input type="checkbox" checked={mountingHoles.enabled} onChange={(e) => setMountingHoles({ enabled: e.target.checked })} data-testid="mounting-holes-enabled" />
        Ajouter des trous de fixation (mur / support)
      </label>
      {mountingHoles.enabled && (
        <>
          <div className="ps-field">
            <label>Disposition</label>
            <select value={mountingHoles.pattern} onChange={(e) => setMountingHoles({ pattern: e.target.value })} className="ps-select">
              <option value="corners">4 coins</option>
              <option value="center">Centre uniquement</option>
              <option value="topcenter">Haut centre</option>
              <option value="bottomcenter">Bas centre</option>
            </select>
          </div>
          <Slider label="Ø trou (vis M)" suffix=" mm" value={mountingHoles.diameter} min={2} max={12} step={0.5}
            onChange={(v) => setMountingHoles({ diameter: v })} />
          <label className="ps-toggle">
            <input type="checkbox" checked={mountingHoles.countersink} onChange={(e) => setMountingHoles({ countersink: e.target.checked })} />
            Lamage / fraisage (tête de vis encastrée)
          </label>
          {mountingHoles.countersink && (
            <>
              <Slider label="Ø lamage" suffix=" mm" value={mountingHoles.countersinkDiameter} min={5} max={20} step={0.5}
                onChange={(v) => setMountingHoles({ countersinkDiameter: v })} />
              <Slider label="Profondeur lamage" suffix=" mm" value={mountingHoles.countersinkDepth} min={1} max={5} step={0.5}
                onChange={(v) => setMountingHoles({ countersinkDepth: v })} />
            </>
          )}
        </>
      )}

      {/* ===== Trou passage câble (LED) ===== */}
      <div className="ps-section-title">Passage de câble (LED)</div>
      <label className="ps-toggle">
        <input type="checkbox" checked={cableHole.enabled} onChange={(e) => setCableHole({ enabled: e.target.checked })} disabled={!ledOn} data-testid="cable-hole-enabled" />
        Trou de passage câble alim LED {!ledOn && '(activez les LEDs)'}
      </label>
      {cableHole.enabled && (
        <>
          <Slider label="Ø passage câble" suffix=" mm" value={cableHole.diameter} min={4} max={25} step={0.5}
            onChange={(v) => setCableHole({ diameter: v })} />
          <div className="ps-field">
            <label>Position</label>
            <select value={cableHole.position} onChange={(e) => setCableHole({ position: e.target.value })} className="ps-select">
              <option value="bottom-center">Bas centré</option>
              <option value="bottom-left">Bas gauche</option>
              <option value="bottom-right">Bas droite</option>
              <option value="back-center">Arrière centré</option>
            </select>
          </div>
        </>
      )}
    </div>
  );
}

export default function Sidebar() {
  const {
    inputMode, setInputMode, fontIndex, setFontIndex,
    structureType, setStructureType,
    dimensions, setDimension,
    light, setLight,
    reset,
    segments, activeSegmentId, setActiveSegment,
    updateSegmentText, updateSegmentColor, updateSegmentMaterial,
    addSegment, removeSegment, moveSegment,
    splitSegmentIntoLetters, toggleSegmentDetached, setSegmentGap,
    partsVisibility, togglePartVisibility,
    ledOn, toggleLed, ledDensity, setLedDensity,
    exploded, toggleExploded, explodeAmount, setExplodeAmount,
    background, setBackground,
    standoffs, toggleStandoffs, setStandoffs,
    pricing, setPricing,
  } = useEditorStore();

  const [fileName, setFileName] = useState('');

  const activeSegment = useMemo(
    () => segments.find((s) => s.id === activeSegmentId) || segments[0],
    [segments, activeSegmentId]
  );

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFileName(f.name);
    toast.success(`Fichier chargé : ${f.name}`);
  };

  const ledInfo = useMemo(() => estimateLedLength(segments, dimensions, ledDensity), [segments, dimensions, ledDensity]);
  const structInfo = STRUCTURE_TYPES_FR.find((s) => s.id === structureType);

  return (
    <aside className="editor-sidebar">
      <ProjectsPanel />
      <ProjectMetaPanel />
      {/* ===== Forme à modéliser ===== */}
      <div className="panel-card">
        <h3>Forme à modéliser <HelpDot tip="Tapez un texte ou importez un SVG" /></h3>
        <div className="tab-toggle">
          <button className={inputMode === 'svg' ? 'active' : ''} onClick={() => setInputMode('svg')}>
            <FileType size={14} /> SVG
          </button>
          <button className={inputMode === 'text' ? 'active' : ''} onClick={() => setInputMode('text')}>
            <Type size={14} /> Texte
          </button>
        </div>
        {inputMode === 'svg' ? (
          <>
            <label className="field-label" style={{ marginTop: 12 }}>Fichier SVG</label>
            <label className="file-input-wrap">
              <span className="file-btn">Choisir un fichier</span>
              <span className="file-name">{fileName || 'Aucun fichier'}</span>
              <input type="file" accept=".svg" style={{ display: 'none' }} onChange={onFileChange} />
            </label>
          </>
        ) : (
          <>
            <label className="field-label" style={{ marginTop: 12 }}>Police</label>
            <select className="text-input" value={fontIndex} onChange={(e) => setFontIndex(parseInt(e.target.value))}>
              {FONTS.map((f, i) => (
                <option key={f.name} value={i} style={{ background: 'var(--bg-panel)' }}>{f.name}</option>
              ))}
            </select>
          </>
        )}
        <button className="btn-clear" onClick={() => { reset(); setFileName(''); toast.success('Réinitialisé'); }}>
          <Trash2 size={14} /> Réinitialiser
        </button>
      </div>

      {/* ===== Segments ===== */}
      <div className="panel-card">
        <h3><Tag size={14} /> Segments de texte <HelpDot tip="Découpez votre texte en segments multi-couleurs" /></h3>
        <div className="panel-subtitle">Chaque segment peut avoir ses propres couleurs et matériaux</div>
        <div className="segments-list">
          {segments.map((seg, idx) => (
            <div key={seg.id} className={`segment-block ${seg.detached ? 'detached' : ''}`}>
              <div
                className={`segment-row ${activeSegmentId === seg.id ? 'active' : ''}`}
                onClick={() => setActiveSegment(seg.id)}
              >
                <div className="segment-colors">
                  <span className="seg-dot" style={{ background: seg.colors.front }} title="Face" />
                  <span className="seg-dot" style={{ background: seg.colors.walls }} title={structureType === 'B' ? 'Socle' : 'Parois'} />
                  {structureType !== 'B' && (
                    <span className="seg-dot" style={{ background: seg.colors.back }} title="Fond" />
                  )}
                </div>
                <input
                  className="segment-text"
                  value={seg.text}
                  onChange={(e) => updateSegmentText(seg.id, e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  placeholder="Texte..."
                />
                <button className="icon-mini" disabled={idx === 0} onClick={(e) => { e.stopPropagation(); moveSegment(seg.id, -1); }} title="Monter">
                  <ArrowUp size={12} />
                </button>
                <button className="icon-mini" disabled={idx === segments.length - 1} onClick={(e) => { e.stopPropagation(); moveSegment(seg.id, 1); }} title="Descendre">
                  <ArrowDown size={12} />
                </button>
                <button
                  className="icon-mini danger"
                  disabled={segments.length <= 1}
                  onClick={(e) => { e.stopPropagation(); removeSegment(seg.id); }}
                  title="Supprimer"
                ><Trash2 size={12} /></button>
              </div>
              <div className="segment-actions">
                <button
                  className="seg-action-btn"
                  disabled={(seg.text || '').length < 2}
                  onClick={() => {
                    splitSegmentIntoLetters(seg.id);
                    toast.success(`Segment "${seg.text}" scindé en ${seg.text.length} lettre(s)`);
                  }}
                  title="Scinder en une lettre par segment (couleurs individuelles)"
                  data-testid={`split-segment-${idx}`}
                >
                  <Scissors size={12} /> Scinder en lettres
                </button>
                <button
                  className={`seg-action-btn ${seg.detached ? 'active' : ''}`}
                  onClick={() => {
                    toggleSegmentDetached(seg.id);
                    toast.success(seg.detached ? 'Élément rattaché' : 'Élément détaché du logo');
                  }}
                  title="Détacher : devient un élément séparé (espace + fichiers de production indépendants)"
                  data-testid={`detach-segment-${idx}`}
                >
                  {seg.detached ? <Unlink2 size={12} /> : <Link2 size={12} />} {seg.detached ? 'Détaché' : 'Rattaché'}
                </button>
                {seg.detached && (
                  <div className="seg-gap-control">
                    <label>Écart</label>
                    <input
                      type="number" min={0} max={500} step={5}
                      value={seg.gapAfterMm}
                      onChange={(e) => setSegmentGap(seg.id, parseInt(e.target.value) || 0)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <span>mm</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
        <button className="btn-secondary" onClick={addSegment} style={{ marginTop: 10 }}>
          <Plus size={14} /> Ajouter un segment
        </button>
      </div>

      {/* ===== Matériaux & couleurs du segment actif ===== */}
      {activeSegment && (
        <div className="panel-card">
          <h3><Palette size={14} /> Matériaux & couleurs <HelpDot tip="Matériau et couleur par pièce du segment sélectionné" /></h3>
          <div className="panel-subtitle">Segment actif : <b style={{ color: 'var(--accent)' }}>{activeSegment.text || '—'}</b></div>

          <PartEditor
            partKey="front" label="Face avant" segment={activeSegment}
            onChangeMaterial={(k, m) => updateSegmentMaterial(activeSegment.id, k, m)}
            onChangeColor={(k, c) => updateSegmentColor(activeSegment.id, k, c)}
          />
          <PartEditor
            partKey="walls"
            label={structureType === 'B' ? 'Socle (parois + fond — pièce unique)' : 'Parois (base / côtés)'}
            segment={activeSegment}
            onChangeMaterial={(k, m) => {
              updateSegmentMaterial(activeSegment.id, k, m);
              // Boîtier : le fond suit automatiquement le socle (pièce unique)
              if (structureType === 'B') updateSegmentMaterial(activeSegment.id, 'back', m);
            }}
            onChangeColor={(k, c) => {
              updateSegmentColor(activeSegment.id, k, c);
              if (structureType === 'B') updateSegmentColor(activeSegment.id, 'back', c);
            }}
          />
          {structureType !== 'B' && (
            <PartEditor
              partKey="back" label="Fond (plaque arrière)" segment={activeSegment}
              onChangeMaterial={(k, m) => updateSegmentMaterial(activeSegment.id, k, m)}
              onChangeColor={(k, c) => updateSegmentColor(activeSegment.id, k, c)}
            />
          )}
        </div>
      )}

      {/* ===== Type de structure ===== */}
      <div className="panel-card">
        <h3><Box size={14} /> Type de structure</h3>
        <div className="panel-subtitle">Mode d'assemblage de la face avant</div>
        {STRUCTURE_TYPES_FR.map((s) => (
          <div
            key={s.id}
            className={`structure-option ${structureType === s.id ? 'selected' : ''}`}
            onClick={() => { setStructureType(s.id); toast.success(`${s.label}`); }}
          >
            <span className="struct-icon"><Box size={16} /></span>
            <span className="struct-letter">{s.icon}</span>
            <span className="struct-label">{s.label}</span>
            <ChevronRight size={16} className="struct-arrow" />
          </div>
        ))}
        {structInfo && (
          <div className="info-banner" style={{ marginTop: 10 }}>
            <b>Assemblage</b>
            <span>{structInfo.assemblyLabel}</span>
          </div>
        )}
      </div>

      {/* ===== Dimensions ===== */}
      <div className="panel-card">
        <h3><Ruler size={14} /> Dimensions</h3>
        <Slider label="Hauteur lettre " suffix=" mm" value={dimensions.height} min={20} max={500}
          onChange={(v) => setDimension('height', v)} />
        <Slider label="Profondeur " suffix=" mm" value={dimensions.depth} min={5} max={150}
          onChange={(v) => setDimension('depth', v)} />
        <Slider label="Épaisseur paroi " suffix=" mm" value={dimensions.wallThickness} min={1} max={15} step={0.5}
          onChange={(v) => setDimension('wallThickness', v)} />
        <Slider label="Épaisseur acrylique " suffix=" mm" value={dimensions.acrylicThickness} min={1} max={20} step={0.5}
          onChange={(v) => setDimension('acrylicThickness', v)} />
      </div>

      {/* ===== Calques (vue éclatée) ===== */}
      <div className="panel-card">
        <h3><Layers size={14} /> Calques / Vue éclatée <HelpDot tip="Cocher pour afficher et écarter les pièces" /></h3>
        <button
          className={`led-toggle ${exploded ? 'on' : ''}`}
          onClick={toggleExploded}
          style={{ marginBottom: 12 }}
        >
          <Layers size={14} /> {exploded ? 'VUE ÉCLATÉE' : 'VUE ASSEMBLÉE'}
        </button>
        <Slider label="Écart entre pièces " value={explodeAmount} min={0} max={3} step={0.1}
          onChange={setExplodeAmount} />
        <div className="panel-subtitle" style={{ marginTop: 10 }}>Pièces visibles dans la scène</div>
        {(structureType === 'B'
          ? [
              { key: 'front', label: 'Face avant', color: 'var(--accent)' },
              { key: 'walls', label: 'Socle (parois + fond)', color: '#a855f7', alsoToggle: 'back' },
            ]
          : [
              { key: 'front', label: 'Face avant', color: 'var(--accent)' },
              { key: 'walls', label: 'Parois (extrusion)', color: '#a855f7' },
              { key: 'back', label: 'Fond (plaque arrière)', color: '#60a5fa' },
            ]
        ).map((p) => (
          <label key={p.key} className="layer-row" onClick={(e) => e.stopPropagation()}>
            <input
              type="checkbox"
              checked={partsVisibility[p.key] !== false}
              onChange={() => {
                togglePartVisibility(p.key);
                if (p.alsoToggle) togglePartVisibility(p.alsoToggle);
              }}
            />
            <span className="layer-icon" style={{ background: p.color }}>
              {partsVisibility[p.key] !== false ? <Eye size={11} /> : <EyeOff size={11} />}
            </span>
            <span className="layer-label">{p.label}</span>
          </label>
        ))}
      </div>

      {/* ===== Fixation murale (entretoises) ===== */}
      <div className="panel-card">
        <h3><Box size={14} /> Fixation murale <HelpDot tip="Entretoises pour fixer l'enseigne au mur" /></h3>
        <div className="panel-subtitle">Activer pour visualiser les entretoises de fixation</div>
        <button
          className={`led-toggle ${standoffs?.enabled ? 'on' : ''}`}
          onClick={toggleStandoffs}
        >
          <Box size={14} /> {standoffs?.enabled ? 'AVEC ENTRETOISES' : 'PLAQUÉ AU MUR'}
        </button>
        {standoffs?.enabled && (
          <>
            <Slider label="Nombre par élément" value={Math.max(2, standoffs.count || 2)} min={2} max={8} step={1}
              onChange={(v) => setStandoffs({ count: v })} />
            <Slider label="Hauteur entretoise " suffix=" mm" value={standoffs.height} min={5} max={80} step={1}
              onChange={(v) => setStandoffs({ height: v })} />
            <Slider label="Diamètre " suffix=" mm" value={standoffs.diameter} min={5} max={30} step={1}
              onChange={(v) => setStandoffs({ diameter: v })} />
            <div className="ps-note">Minimum 2 entretoises par élément, fixées à la base du socle. Les trous correspondants sont automatiquement percés dans le fond (visibles en 3D et indiqués dans le dossier de production).</div>
          </>
        )}
      </div>

      {/* ===== Mise en situation ===== */}
      <div className="panel-card">
        <h3><Palette size={14} /> Mise en situation <HelpDot tip="Visualiser l'enseigne dans son environnement" /></h3>
        <div className="panel-subtitle">Choisissez un fond mural pour le rendu 3D</div>
        <div className="bg-grid">
          {[
            { id: 'none', label: 'Aucun', bg: 'linear-gradient(135deg, #1a1a20, #050507)' },
            { id: 'color', label: 'Couleur', bg: background.color || '#3a3a42' },
            { id: 'brick', label: 'Brique', bg: 'linear-gradient(135deg, #7a3018, #4a1808)' },
            { id: 'concrete', label: 'Béton', bg: 'linear-gradient(135deg, #888, #444)' },
            { id: 'wood', label: 'Bois', bg: 'linear-gradient(135deg, #a07040, #5a3818)' },
            { id: 'green', label: 'Mur végétal', bg: 'linear-gradient(135deg, #3aaa35, #1a5a1a)' },
          ].map((b) => (
            <button
              key={b.id}
              className={`bg-chip ${background.type === b.id ? 'selected' : ''}`}
              onClick={() => setBackground({ type: b.id })}
              title={b.label}
            >
              <span className="bg-swatch" style={{ background: b.bg }} />
              <span className="bg-label">{b.label}</span>
            </button>
          ))}
        </div>
        {background.type === 'color' && (
          <div className="slider-row" style={{ marginTop: 10 }}>
            <label>Couleur du mur</label>
            <input type="color" className="color-input"
              value={background.color}
              onChange={(e) => setBackground({ color: e.target.value })}
            />
          </div>
        )}
        {['brick', 'concrete', 'wood', 'green'].includes(background.type) && (
          <Slider
            label="Taille du motif"
            suffix=" mm/tuile"
            value={background.textureScaleMm ?? 300}
            min={30} max={2000} step={10}
            onChange={(v) => setBackground({ textureScaleMm: v })}
          />
        )}
      </div>

      {/* ===== Éclairage LED ===== */}
      <div className="panel-card">
        <h3><Zap size={14} /> Éclairage LED <HelpDot tip="Aperçu allumé + calcul ruban LED nécessaire" /></h3>
        <button
          className={`led-toggle ${ledOn ? 'on' : ''}`}
          onClick={toggleLed}
        >
          <Zap size={14} /> {ledOn ? 'LED ALLUMÉ' : 'LED ÉTEINT'}
        </button>
        <Slider label="Densité ruban " suffix=" LED/m" value={ledDensity} min={30} max={120} step={10}
          onChange={setLedDensity} />
        <div className="led-info">
          <div className="led-info-row">
            <span>Ruban LED nécessaire</span>
            <b>{ledInfo.lengthM} m</b>
          </div>
          <div className="led-info-row">
            <span>Nombre de LEDs</span>
            <b>{ledInfo.ledCount}</b>
          </div>
          <div className="led-info-row">
            <span>Espacement serpentin</span>
            <b>{ledInfo.spacing} mm</b>
          </div>
        </div>
      </div>

      {/* ===== Éclairage scène ===== */}
      <div className="panel-card">
        <h3><Lightbulb size={14} /> Éclairage de scène</h3>
        <Slider label="Intensité " value={light.intensity} min={0} max={3} step={0.05}
          onChange={(v) => setLight('intensity', v)} />
        <Slider label="Position horizontale " suffix="°" value={light.horizontal} min={0} max={360}
          onChange={(v) => setLight('horizontal', v)} />
        <Slider label="Hauteur " value={light.height} min={0} max={500}
          onChange={(v) => setLight('height', v)} />
      </div>

      {/* ===== Paramètres impression 3D (Slicer) ===== */}
      <div className="panel-card">
        <h3><Printer size={14} /> Paramètres impression 3D <HelpDot tip="Réglages slicer (Cura/PrusaSlicer/OrcaSlicer). Inclus dans le ZIP d'export." /></h3>
        <PrintSettingsPanel />
      </div>

      {/* ===== Prix, marge & prix de vente ===== */}
      <div className="panel-card">
        <h3><Tag size={14} /> Prix & marge</h3>
        {(() => {
          const costs = estimateCosts(segments, dimensions, structureType, pricing);
          return (
            <>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.8 }}>
                <div>Coût matières : <b style={{ color: 'var(--text)' }}>{costs.materialsCost} €</b></div>
                <div>Coût main d'œuvre : <b style={{ color: 'var(--text)' }}>{costs.laborCost} €</b> ({costs.laborHours.toFixed(2)} h × {pricing.laborPerHour} €)</div>
                <div>Sous-total (revient) : <b style={{ color: 'var(--text)' }}>{(costs.materialsCost + costs.laborCost).toFixed(2)} €</b></div>
              </div>
              <Slider label="Taux de marge" suffix=" %" value={pricing.marginPct} min={0} max={200} step={1}
                onChange={(v) => setPricing({ marginPct: v })} />
              <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.8, marginTop: 6 }}>
                <div>Marge : <b style={{ color: 'var(--accent)' }}>+{costs.marginAmount} €</b></div>
                <div style={{ padding: '6px 0', borderTop: '1px dashed var(--border)', borderBottom: '1px dashed var(--border)', margin: '6px 0' }}>
                  Prix de vente HT : <b style={{ color: 'var(--accent)', fontSize: 15 }}>{costs.totalHT} €</b>
                </div>
                <div>Prix de vente TTC (TVA 20%) : <b style={{ color: 'var(--text)' }}>{costs.totalTTC} €</b></div>
              </div>
            </>
          );
        })()}
      </div>

      {/* ===== Info & Export ===== */}
      <div className="panel-card">
        <h3><Info size={14} /> Informations</h3>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.8 }}>
          <div>Segments : <b style={{ color: 'var(--text)' }}>{segments.length}</b></div>
          <div>Texte total : <b style={{ color: 'var(--text)' }}>{segments.map((s) => s.text).join('')}</b></div>
          <div>Hauteur : <b style={{ color: 'var(--text)' }}>{dimensions.height} mm</b></div>
          <div>Profondeur : <b style={{ color: 'var(--text)' }}>{dimensions.depth} mm</b></div>
        </div>
      </div>

      <div className="panel-card">
        <h3><FileBox size={14} /> Export</h3>
        <div className="export-grid">
          <button
            className="export-btn export-zip"
            data-testid="export-production-zip"
            onClick={async () => {
              console.log('[ZIP] click handler start');
              try {
                toast.info('Génération du dossier de production...');
                const state = useEditorStore.getState();
                console.log('[ZIP] state.segments =', state.segments?.length, 'structureType=', state.structureType);
                await new Promise((r) => setTimeout(r, 80));
                console.log('[ZIP] calling exportProductionZip...');
                const res = await exportProductionZip(state);
                console.log('[ZIP] export done:', res);
                toast.success(`Dossier généré : ${res.pieceCount} pièce(s) — ${res.fileName}`);
              } catch (e) {
                console.error('[ZIP] export failed:', e);
                toast.error(`Erreur génération ZIP : ${e.message || e}`);
              }
            }}
          >
            <b>ZIP</b><span>Dossier production (STL + paramètres)</span>
          </button>
          <button
            className="export-btn"
            data-testid="export-pdf-btn"
            onClick={async () => {
              try {
                const state = useEditorStore.getState();
                toast.info('Génération du cahier des charges...');
                await new Promise((r) => setTimeout(r, 100));
                const filename = await exportProjectPDF(state);
                toast.success(`PDF généré : ${filename}`);
              } catch (e) {
                console.error(e);
                toast.error('Erreur génération PDF');
              }
            }}
          >
            <b>PDF</b><span>Cahier des charges</span>
          </button>
          <button className="export-btn" onClick={() => toast.success('Capture PNG — utilisez le bouton appareil photo')}>
            <b>PNG</b><span>Aperçu rendu</span>
          </button>
        </div>
      </div>
    </aside>
  );
}
